import api from './api';

// ---------- REFERENCE IMAGES ----------

export interface CharacterImage {
    characterName: string;
    prompt: string;
    hasImage: boolean;
    updatedAt: string | null;
    copiedFromPart?: number | null; // SERIES: the earlier part this picture came from
}

export interface ReferenceImagesInfo {
    characters: CharacterImage[];
    canGenerate: boolean;
    usedThisWeek: number;
    weeklyLimit: number;
    resetsOn: string;
}

export const getReferenceImages = async (scriptId: number): Promise<ReferenceImagesInfo> => {
    const response = await api.get(`/api/scripts/${scriptId}/reference-images`);
    return response.data;
};

export const generateReferenceImage = async (scriptId: number, characterName: string): Promise<CharacterImage> => {
    const response = await api.post(`/api/scripts/${scriptId}/reference-images/generate`, null, {
        params: { character: characterName },
        timeout: 120000,
    });
    return response.data;
};

// Loads a saved picture and returns a link the page can show
export const getReferenceImageUrl = async (scriptId: number, characterName: string): Promise<string> => {
    const response = await api.get(`/api/scripts/${scriptId}/reference-images/image`, {
        params: { character: characterName },
        responseType: 'blob',
    });
    return window.URL.createObjectURL(new Blob([response.data], { type: 'image/png' }));
};

// ---------- VIDEO SHOTS ----------

export interface VideoClip {
    shotNumber: number;
    status: 'PENDING' | 'READY' | 'FAILED';
    errorMessage: string | null;
    updatedAt: string | null;
}

export interface VideoCredits {
    credits: number;
    freeClipsLeft: number;
    freeClipsPerMonth: number;
    canUseVideo: boolean;
}

export interface CreditPack {
    pack: string;
    credits: number;
    priceNaira: number;
}

export const getVideoCredits = async (): Promise<VideoCredits> => {
    const response = await api.get('/api/video/credits');
    return response.data;
};

export const getVideoClips = async (scriptId: number): Promise<VideoClip[]> => {
    const response = await api.get(`/api/scripts/${scriptId}/videos`);
    return response.data;
};

export const generateVideoClip = async (scriptId: number, shotNumber: number): Promise<VideoClip> => {
    const response = await api.post(`/api/scripts/${scriptId}/videos/${shotNumber}/generate`, null, {
        timeout: 120000,
    });
    return response.data;
};

// Loads a finished clip and returns a link the video player can use
export const getVideoClipUrl = async (scriptId: number, shotNumber: number): Promise<string> => {
    const response = await api.get(`/api/scripts/${scriptId}/videos/${shotNumber}/file`, {
        responseType: 'blob',
    });
    return window.URL.createObjectURL(new Blob([response.data], { type: 'video/mp4' }));
};

// ---------- BUYING CREDITS ----------

export const getCreditPacks = async (): Promise<CreditPack[]> => {
    const response = await api.get('/api/video/credits/packs');
    return response.data;
};

// Starts a Paystack payment; returns the payment page link
export const buyCreditPack = async (pack: string): Promise<string> => {
    const response = await api.post('/api/video/credits/buy', null, { params: { pack } });
    return response.data.authorizationUrl;
};

// Confirms the payment after Paystack sends the user back; returns the new credit balance
export const confirmCreditPurchase = async (reference: string): Promise<number> => {
    const response = await api.post('/api/video/credits/confirm', null, { params: { reference } });
    return response.data.credits;
};

// ---------- THUMBNAILS ----------

export interface ThumbnailIdea {
    ideaNumber: number;
    idea: string;
    hasImage: boolean;
}

export interface ThumbnailsInfo {
    ideas: ThumbnailIdea[];
    canGenerate: boolean;
    usedThisWeek: number;
    weeklyLimit: number;
    resetsOn: string;
}

export const getThumbnails = async (scriptId: number): Promise<ThumbnailsInfo> => {
    const response = await api.get(`/api/scripts/${scriptId}/thumbnails`);
    return response.data;
};

export const generateThumbnail = async (scriptId: number, ideaNumber: number): Promise<ThumbnailIdea> => {
    const response = await api.post(`/api/scripts/${scriptId}/thumbnails/${ideaNumber}/generate`, null, {
        timeout: 120000,
    });
    return response.data;
};

// Loads a saved thumbnail and returns a link the page can show
export const getThumbnailUrl = async (scriptId: number, ideaNumber: number): Promise<string> => {
    const response = await api.get(`/api/scripts/${scriptId}/thumbnails/${ideaNumber}/image`, {
        responseType: 'blob',
    });
    return window.URL.createObjectURL(new Blob([response.data], { type: 'image/png' }));
};